import { useContext, useMemo } from 'react';
import {
  Box,
  Tabs,
} from '@usealloy/component-library';
import type { TabsProps } from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';

import { RiskRatingCard } from '../../RiskRatingCard/RiskRatingCard';
import { RiskScoreTabLabel } from '../../RiskRatingCard/RiskScoreTabLabel';
import type { CoreRiskData } from '../../RiskRatingCard/useRiskData';
import { useGetWatchlistMatches } from '../../watchlist-review/hooks/getWatchlistMatches';
import { ReviewTabCount } from '../../watchlist-review/styles';
import { useEventTypeEvaluationsContext } from '../context';
import { WatchlistMatches } from '../tabs';
import { BusinessUpdatedOverview } from './BusinessUpdatedOverview';

interface PersonUpdatedTabsProps {
  canViewRiskFormula: boolean;
  riskData: CoreRiskData;
  shouldRenderRiskTab: boolean;
  shouldRenderWatchlistTab: boolean;
}

export const BusinessUpdatedTabs = ({
  canViewRiskFormula, riskData, shouldRenderRiskTab, shouldRenderWatchlistTab,
}: PersonUpdatedTabsProps) => {
  const { evaluation } = useEventTypeEvaluationsContext();
  const {
    evaluation_token: evaluationToken,
    entity_token: entityToken,
  } = evaluation;
  const { typhonDomain } = useContext(UserContext);

  const [{
    data: { matches = [] },
    isLoading: isLoadingWatchlistMatches,
  }] = useGetWatchlistMatches({
    evaluations: [{
      evaluationToken,
      entityToken,
    }],
    typhonDomain,
    showLatestDecisions: true,
  });

  const {
    riskFactors, riskFormula, riskRangeValues, totalRisk, riskRatingColor, riskRatingLabel,
  } = riskData;

  const tabs = useMemo(() => {
    const reviewMatchesLength = matches
      ? matches
        .filter(({ status, is_suppressed }) => (status === 'Needs Review' && !is_suppressed))
        .length
      : 0;

    const allTabs: TabsProps['tabs'] = [
      {
        label: 'Overview',
        value: 'overview',
        content: <BusinessUpdatedOverview />,
      },
    ];

    if (shouldRenderWatchlistTab) {
      allTabs.push({
        label: (
          <Box direction="row" gap={1}>
            Watchlist Matches
            {!isLoadingWatchlistMatches && reviewMatchesLength > 0 && (
              <ReviewTabCount status="danger">
                <Box justifyContent="center" alignItems="center">
                  {reviewMatchesLength}
                </Box>
              </ReviewTabCount>
            )}
          </Box>
        ),
        value: 'watchlist',
        content: <WatchlistMatches evaluation={evaluation} />,
      });
    }

    if (shouldRenderRiskTab) {
      allTabs.push({
        label: (
          <RiskScoreTabLabel
            riskRatingColor={riskRatingColor}
            riskRatingLabel={riskRatingLabel}
          />
        ),
        value: 'risk-score',
        content: (
          <RiskRatingCard
            canViewRiskFormula={canViewRiskFormula}
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            evaluationType={evaluation.evaluation_type}
            riskFactors={riskFactors}
            riskOutputFormula={riskFormula}
            riskRanges={riskRangeValues}
            riskValue={totalRisk}
            riskValueColor={riskRatingColor}
            riskValueLabel={riskRatingLabel}
            variant="entityRisk"
            workflowToken={evaluation.application_token}
            workflowVersion={evaluation.public_version}
          />
        ),
      });
    }

    return allTabs;
  }, [
    canViewRiskFormula,
    evaluation,
    isLoadingWatchlistMatches,
    matches,
    riskFactors,
    riskFormula,
    riskRangeValues,
    riskRatingColor,
    riskRatingLabel,
    shouldRenderRiskTab,
    shouldRenderWatchlistTab,
    totalRisk,
  ]);

  return (
    <Tabs style={{ width: '100%' }} tabs={tabs} />
  );
};
